import { useEffect, useRef, useState } from "react";
import { Download, LogOut, Pencil, Trash2, Upload, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";

const STATUS_STYLE = {
  confirmado: "border-sage bg-sage-light text-sage-dark",
  ausente: "border-red-200 bg-red-50 text-red-800",
  pendente: "border-gold-border bg-white text-muted",
};

const STATUS_LABEL = { confirmado: "Confirmado", ausente: "Ausente", pendente: "Pendente" };
const CAT_LABEL = { adulto: "Adulto", crianca: "Criança", bebe: "Bebê" };

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-5" data-testid="admin-login">
      <form onSubmit={submit} className="paper-card gold-frame w-full max-w-md p-10">
        <img src="/images/monograma.png" alt="Monograma de Rodrigo e Talita" className="mx-auto h-16 w-auto object-contain" />
        <h1 className="mt-4 text-center font-script text-5xl text-ink">Área do casal</h1>
        <div className="mt-8 space-y-4">
          <input
            type="email"
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail"
            required
            className="w-full border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none focus:border-sage"
          />
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha"
            required
            className="w-full border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none focus:border-sage"
          />
          {error && (
            <p className="border border-red-200 bg-red-50 px-4 py-3 font-sans text-xs text-red-700" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button type="submit" data-testid="admin-login-submit" disabled={busy} className="btn-gold w-full">
            {busy ? "Entrando..." : "Entrar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function GuestsTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = (query = q) =>
    api
      .get("/admin/guests", { params: { q: query } })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const removeRsvp = async (rsvpId) => {
    if (!window.confirm("Apagar a resposta deste convite? O convidado poderá responder de novo.")) return;
    await api.delete(`/admin/rsvps/${rsvpId}`).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    load();
  };

  const saveGuest = async () => {
    try {
      await api.put(`/admin/guests/${editing.guest_id}`, {
        name: editing.name,
        limits: {
          adultos: Number(editing.adultos),
          criancas: Number(editing.criancas),
          bebes: Number(editing.bebes),
        },
      });
      toast.success("Convite atualizado.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const header = "Convidado;Status;Limite Adultos;Limite Crianças;Limite Bebês;Telefone;Telefone de;Quem confirmou;Mensagem;Atualizado em\n";
    const rows = data.rows
      .map((r) =>
        [
          r.name,
          STATUS_LABEL[r.status],
          r.limits.adultos,
          r.limits.criancas,
          r.limits.bebes,
          r.telefone || "",
          r.telefone_pessoa || "",
          r.presentes.map((p) => `${p.name} (${CAT_LABEL[p.categoria]})`).join(" | "),
          (r.mensagem || "").replace(/\n/g, " "),
          r.updated_at ? new Date(r.updated_at).toLocaleString("pt-BR") : "",
        ].join(";")
      )
      .join("\n");
    const blob = new Blob(["﻿" + header + rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "confirmacoes-presenca.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return null;
  const s = data.summary;

  return (
    <div data-testid="admin-rsvps-tab">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: "Convites", value: s.convites, id: "summary-convites" },
          { label: "Confirmados", value: s.confirmados, id: "summary-confirmados" },
          { label: "Ausentes", value: s.ausentes, id: "summary-ausentes" },
          { label: "Pendentes", value: s.pendentes, id: "summary-pendentes" },
        ].map((c) => (
          <div key={c.id} className="paper-card p-5 text-center">
            <p className="font-serif text-4xl text-ink" data-testid={c.id}>{c.value}</p>
            <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.24em] text-sage-dark">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="paper-card mt-4 grid grid-cols-2 gap-4 p-5 text-center sm:grid-cols-4" data-testid="category-totals">
        {[
          { label: "Adultos presentes", value: s.adultos, id: "tot-adultos" },
          { label: "Crianças presentes", value: s.criancas, id: "tot-criancas" },
          { label: "Bebês presentes", value: s.bebes, id: "tot-bebes" },
          { label: "Total de pessoas", value: s.total_presentes, id: "tot-presentes" },
        ].map((c) => (
          <div key={c.id}>
            <p className="font-serif text-3xl text-gold-deep" data-testid={c.id}>{c.value}</p>
            <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.2em] text-muted">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <input
          data-testid="admin-guest-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Pesquisar convidado..."
          className="w-full max-w-xs border border-gold-border bg-white px-4 py-2.5 font-sans text-sm outline-none focus:border-sage"
        />
        <button onClick={exportCsv} data-testid="rsvp-csv-export" className="btn-outline px-5 py-2.5 text-[10px]">
          <Download size={12} /> Exportar CSV
        </button>
      </div>

      <div className="paper-card mt-4 overflow-x-auto">
        <table className="w-full min-w-[860px] text-left" data-testid="guests-table">
          <thead>
            <tr className="border-b border-gold-border font-sans text-[10px] uppercase tracking-[0.22em] text-sage-dark">
              <th className="px-5 py-4">Convidado</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Contato</th>
              <th className="px-5 py-4 text-center">Limite (A/C/B)</th>
              <th className="px-5 py-4">Quem confirmou</th>
              <th className="px-5 py-4">Mensagem</th>
              <th className="px-5 py-4">Atualizado</th>
              <th className="px-5 py-4 text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) =>
              editing && editing.guest_id === r.guest_id ? (
                <tr key={r.guest_id} className="border-b border-gold-border/50 bg-sage-light/40 font-sans text-sm">
                  <td className="px-5 py-3" colSpan={7}>
                    <div className="flex flex-wrap items-end gap-3" data-testid="guest-edit-row">
                      <label className="flex-1 font-sans text-[10px] uppercase tracking-[0.18em] text-muted">
                        Nome
                        <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="guest-edit-name" className="mt-1 w-full border border-gold-border bg-white px-3 py-2 font-sans text-sm normal-case tracking-normal text-ink outline-none focus:border-sage" />
                      </label>
                      {["adultos", "criancas", "bebes"].map((k) => (
                        <label key={k} className="font-sans text-[10px] uppercase tracking-[0.18em] text-muted">
                          {k}
                          <input type="number" min={k === "adultos" ? 1 : 0} max={20} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} data-testid={`guest-edit-${k}`} className="mt-1 w-20 border border-gold-border bg-white px-3 py-2 font-sans text-sm normal-case tracking-normal text-ink outline-none focus:border-sage" />
                        </label>
                      ))}
                      <button onClick={saveGuest} data-testid="guest-edit-save" className="btn-gold px-5 py-2.5 text-[10px]">Salvar</button>
                      <button onClick={() => setEditing(null)} data-testid="guest-edit-cancel" className="btn-outline px-5 py-2.5 text-[10px]"><X size={12} /></button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={r.guest_id} className="border-b border-gold-border/50 align-top font-sans text-sm font-light" data-testid={`guest-row-${r.guest_id}`}>
                  <td className="px-5 py-4 font-normal text-ink">{r.name}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block border px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLE[r.status]}`} data-testid={`guest-status-${r.guest_id}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-muted" data-testid={`guest-phone-${r.guest_id}`}>
                    {r.telefone ? (
                      <>
                        {r.telefone}
                        {r.telefone_pessoa ? <span className="block text-[10px] uppercase tracking-[0.14em] text-sage-dark">{r.telefone_pessoa}</span> : null}
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-4 text-center text-muted">
                    {r.limits.adultos}/{r.limits.criancas}/{r.limits.bebes}
                  </td>
                  <td className="max-w-[260px] px-5 py-4 text-muted">
                    {r.presentes.length
                      ? r.presentes.map((p, i) => (
                          <span key={i} className="mr-2 inline-block whitespace-nowrap">
                            {p.name} <span className="text-[10px] uppercase tracking-[0.14em] text-sage-dark">({CAT_LABEL[p.categoria]})</span>
                          </span>
                        ))
                      : "—"}
                  </td>
                  <td className="max-w-[180px] px-5 py-4 text-muted">{r.mensagem || "—"}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-muted">
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString("pt-BR") : "—"}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => setEditing({ guest_id: r.guest_id, name: r.name, adultos: r.limits.adultos, criancas: r.limits.criancas, bebes: r.limits.bebes })}
                        data-testid={`guest-edit-btn-${r.guest_id}`}
                        aria-label={`Editar convite de ${r.name}`}
                        className="inline-grid h-8 w-8 place-items-center border border-gold-border text-ink transition-colors hover:bg-sage-light"
                      >
                        <Pencil size={12} />
                      </button>
                      {r.rsvp_id && (
                        <button
                          onClick={() => removeRsvp(r.rsvp_id)}
                          data-testid={`rsvp-delete-btn-${r.rsvp_id}`}
                          aria-label={`Apagar resposta de ${r.name}`}
                          className="inline-grid h-8 w-8 place-items-center border border-gold-border text-red-800 transition-colors hover:bg-red-50"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            )}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center font-serif text-lg italic text-muted">
                  Nenhum convidado encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);

  const load = () =>
    api
      .get("/admin/messages")
      .then(({ data }) => setMessages(data.messages || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const setApproved = async (id, approved) => {
    await api.put(`/admin/messages/${id}`, { approved });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar esta mensagem?")) return;
    await api.delete(`/admin/messages/${id}`);
    load();
  };

  const pending = messages.filter((m) => !m.approved);
  const published = messages.filter((m) => m.approved);

  return (
    <div data-testid="admin-messages-tab">
      <h3 className="font-serif text-2xl text-ink">Aguardando aprovação ({pending.length})</h3>
      <div className="mt-4 space-y-4">
        {pending.map((m) => (
          <div key={m.id} className="paper-card p-6" data-testid="pending-message">
            <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-gold-deep">
              {m.user_name} · {m.date_label}
            </p>
            <p className="mt-3 whitespace-pre-line font-serif text-lg italic text-ink/85">{m.content}</p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setApproved(m.id, true)} data-testid={`message-approve-btn-${m.id}`} className="btn-gold px-5 py-2.5 text-[10px]">
                Aprovar e publicar
              </button>
              <button onClick={() => remove(m.id)} data-testid={`message-delete-btn-${m.id}`} className="btn-outline px-5 py-2.5 text-[10px] text-red-800">
                <Trash2 size={12} /> Apagar
              </button>
            </div>
          </div>
        ))}
        {pending.length === 0 && <p className="font-serif text-lg italic text-muted">Nenhuma mensagem aguardando aprovação.</p>}
      </div>

      <h3 className="mt-10 font-serif text-2xl text-ink">Publicadas ({published.length})</h3>
      <div className="mt-4 space-y-4">
        {published.map((m) => (
          <div key={m.id} className="paper-card p-6">
            <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-sage-dark">
              {m.user_name} · {m.date_label}
            </p>
            <p className="mt-3 whitespace-pre-line font-serif text-lg italic text-ink/85">{m.content}</p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setApproved(m.id, false)} className="btn-outline px-5 py-2.5 text-[10px]">
                Despublicar
              </button>
              <button onClick={() => remove(m.id)} className="btn-outline px-5 py-2.5 text-[10px] text-red-800">
                <Trash2 size={12} /> Apagar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab({ settings, reload }) {
  const [rsvpOpen, setRsvpOpen] = useState(settings.rsvp_open);
  const [afterDeadline, setAfterDeadline] = useState(settings.rsvp_after_deadline || false);
  const [title, setTitle] = useState(settings.featured?.title || "");
  const [artist, setArtist] = useState(settings.featured?.artist || "");
  const [note, setNote] = useState(settings.featured?.note || "");
  const [busy, setBusy] = useState(false);
  const musicInput = useRef(null);
  const galleryInput = useRef(null);
  const [gallery, setGallery] = useState([]);

  const loadGallery = () =>
    api
      .get("/gallery")
      .then(({ data }) => setGallery(data.photos || []))
      .catch(() => {});

  useEffect(() => {
    loadGallery();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        rsvp_open: rsvpOpen,
        rsvp_after_deadline: afterDeadline,
        music_title: title,
        music_artist: artist,
        music_note: note,
      });
      toast.success("Ajustes salvos com sucesso!");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadMusic = async () => {
    const file = musicInput.current?.files?.[0];
    if (!file) {
      toast.error("Escolha um arquivo de áudio primeiro.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/music", fd);
      toast.success("Música atualizada! O player do site já toca o novo arquivo.");
      if (musicInput.current) musicInput.current.value = "";
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadGallery = async () => {
    const files = Array.from(galleryInput.current?.files || []);
    if (!files.length) {
      toast.error("Escolha as fotos do álbum primeiro.");
      return;
    }
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/gallery", fd);
      }
      toast.success("Fotos adicionadas ao álbum da nossa história!");
      if (galleryInput.current) galleryInput.current.value = "";
      loadGallery();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    loadGallery();
  };

  return (
    <div className="space-y-8" data-testid="admin-settings-tab">
      <div className="paper-card p-7">
        <h3 className="font-serif text-2xl text-ink">Confirmação de presença</h3>
        <p className="mt-2 font-sans text-sm font-light text-muted">
          O prazo oficial termina em <strong>03/12/2026</strong>. Após essa data, o formulário só aceita respostas se a reabertura administrativa estiver ativa.
        </p>
        <label className="mt-4 flex items-center gap-3 font-sans text-sm text-ink" data-testid="settings-rsvp-open">
          <input type="checkbox" checked={rsvpOpen} onChange={(e) => setRsvpOpen(e.target.checked)} className="h-4 w-4 accent-[#7E9381]" />
          Confirmações de presença abertas
        </label>
        <label className="mt-3 flex items-center gap-3 font-sans text-sm text-ink" data-testid="settings-rsvp-after-deadline">
          <input type="checkbox" checked={afterDeadline} onChange={(e) => setAfterDeadline(e.target.checked)} className="h-4 w-4 accent-[#7E9381]" />
          Permitir confirmações após o prazo (reabertura administrativa)
        </label>
      </div>

      <div className="paper-card p-7">
        <h3 className="font-serif text-2xl text-ink">Música autoral dos noivos</h3>
        <p className="mt-2 font-sans text-sm font-light text-muted">
          Título, intérprete e um recado curto (opcional) exibidos no player em destaque. Você também pode trocar o arquivo de áudio.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <input data-testid="settings-music-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título da música" className="w-full border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none focus:border-sage" />
          <input data-testid="settings-music-artist" value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Intérprete" className="w-full border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none focus:border-sage" />
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={400} placeholder="Recado curto dos noivos (opcional)" className="mt-4 w-full resize-none border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none focus:border-sage" />
      </div>

      <div className="paper-card p-7">
        <h3 className="font-serif text-2xl text-ink">Áudio da música (MP3)</h3>
        <p className="mt-2 font-sans text-sm font-light text-muted">
          Arquivo atual: <strong>{settings.featured?.url || "—"}</strong>
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input ref={musicInput} type="file" accept="audio/*" data-testid="music-file-input" className="font-sans text-xs text-muted" />
          <button onClick={uploadMusic} disabled={busy} data-testid="music-upload-btn" className="btn-gold px-5 py-2.5 text-[10px]">
            <Upload size={12} /> Enviar novo áudio
          </button>
        </div>
      </div>

      <div className="paper-card p-7">
        <h3 className="font-serif text-2xl text-ink">Álbum — Nossa história</h3>
        <p className="mt-2 font-sans text-sm font-light text-muted">
          Envie as fotos do álbum do casal. Elas aparecem na aba “Nossa História”.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input ref={galleryInput} type="file" accept="image/*" multiple data-testid="gallery-file-input" className="font-sans text-xs text-muted" />
          <button onClick={uploadGallery} disabled={busy} data-testid="gallery-upload-btn" className="btn-gold px-5 py-2.5 text-[10px]">
            <Upload size={12} /> Enviar fotos
          </button>
        </div>
        {gallery.length > 0 && (
          <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-6">
            {gallery.map((p) => (
              <div key={p.id} className="relative" data-testid="gallery-thumb">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="aspect-square w-full border border-gold-border object-cover" />
                <button onClick={() => removePhoto(p.id)} data-testid={`gallery-photo-delete-${p.id}`} aria-label="Remover foto" className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-ink text-paper">
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={save} disabled={busy} data-testid="settings-save-btn" className="btn-gold w-full">
        {busy ? "Salvando..." : "Salvar ajustes"}
      </button>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("rsvps");
  const [settings, setSettings] = useState(null);

  const loadSettings = () =>
    api
      .get("/music")
      .then(({ data }) => setSettings(data))
      .catch(() => {});

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadSettings();
      })
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return <div className="grid min-h-screen place-items-center"><p className="font-serif italic text-muted">Carregando...</p></div>;
  }

  if (user === null) {
    return (
      <>
        <Login onLogin={(u) => { setUser(u); loadSettings(); }} />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gold-border pb-6">
        <div className="flex items-center gap-4">
          <img src="/images/monograma.png" alt="Monograma de Rodrigo e Talita" className="h-12 w-auto object-contain" />
          <div>
            <p className="eyebrow">Área do casal</p>
            <h1 className="mt-1 font-script text-5xl text-ink">Rodrigo &amp; Talita</h1>
          </div>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="btn-outline px-5 py-2.5 text-[10px]">
          <LogOut size={12} /> Sair
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        {[
          { id: "rsvps", label: "Confirmações" },
          { id: "messages", label: "Mensagens" },
          { id: "settings", label: "Ajustes" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`border px-6 py-2.5 font-sans text-[10px] uppercase tracking-[0.24em] transition-colors ${
              tab === t.id ? "border-ink bg-ink text-paper" : "border-gold-border bg-white text-ink hover:bg-sage-light"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "rsvps" && <GuestsTab />}
        {tab === "messages" && <MessagesTab />}
        {tab === "settings" && settings && <SettingsTab settings={settings} reload={loadSettings} />}
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
